/**
 * Why an account is worth a call today (docs/product/phase-0/04-WHY-THIS-ACCOUNT.md).
 *
 * The domain's stated reasons (`collectionPriority`) from the account's own
 * facts — one adapter from a row of the book (`priorityInputFromBookRow`), so
 * Today's list and the account's page give the same reasons — checked against
 * the company's collection settings, which anyone may read, and the account's
 * cheques when this person may see cheques. Never a score, and never called AI.
 */
import { useEffect, useMemo, useState } from 'react';
import {
    collectionPriority, holds, priorityChequeStates, priorityInputFromBookRow,
    type BookRow, type Cheque, type CollectionSettings, type PriorityReason, type TenantContext,
} from '@timely/domain';
import { supabase } from '../lib/supabaseClient';
import { useFormatters, type Formatters } from './useFormatters';
import { useReceivablesSettings } from '../receivables/useReceivablesSettings';

type ChequeFacts = Pick<Cheque, 'status' | 'chequeDate'>;

/** The reasons for one row, most serious first. */
export function priorityReasons(
    row: BookRow, collection: CollectionSettings, cheques: readonly ChequeFacts[],
    f: Pick<Formatters, 'date' | 'today'>, money: (amount: number) => string,
): PriorityReason[] {
    const input = priorityInputFromBookRow(row, value => f.date.inputValue(value), priorityChequeStates(cheques, f.today));
    return collectionPriority(input, collection, f.date.todayValue(), money, day => f.date.long(day)).reasons;
}

/**
 * For a list: the reasons of each row, once the company's settings (and the
 * shown accounts' cheques) have been read — null until then, so no row shows
 * reasons worked out against the wrong thresholds.
 */
export function usePriorityReasons(context: TenantContext, rows: BookRow[]): ((row: BookRow) => PriorityReason[]) | null {
    const { date, today, money } = useFormatters();
    const settings = useReceivablesSettings(context.workspace.id);
    const canSeeCheques = holds(context, 'cheques.view');
    const workspaceId = context.workspace.id;
    const ids = useMemo(() => rows.map(r => r.customer.id), [rows]);
    const key = ids.join(',');
    const [cheques, setCheques] = useState<Map<string, ChequeFacts[]> | null>(null);

    useEffect(() => {
        let current = true;
        if (!canSeeCheques || key === '') { setCheques(new Map()); return; }
        setCheques(null);
        void supabase().from('pdc_cheques').select('customer_id, cheque_date, status')
            .eq('workspace_id', workspaceId).in('customer_id', key.split(',')).in('status', ['Pending', 'Bounced'])
            .then(({ data, error }) => {
                if (!current) return;
                const byAccount = new Map<string, ChequeFacts[]>();
                // A refused read leaves the cheque reasons out rather than the list without reasons.
                for (const r of (error ? [] : data ?? []) as { customer_id: string; cheque_date: string; status: string }[]) {
                    const list = byAccount.get(r.customer_id) ?? [];
                    list.push({ status: r.status as Cheque['status'], chequeDate: new Date(r.cheque_date) });
                    byAccount.set(r.customer_id, list);
                }
                setCheques(byAccount);
            });
        return () => { current = false; };
    }, [key, canSeeCheques, workspaceId]);

    // Settings that could not be read are not the standard ones: no reasons rather than reasons against the wrong thresholds.
    if (settings.loading || settings.error || cheques === null) return null;
    const collection = settings.current.settings.collection;
    return row => priorityReasons(row, collection, cheques.get(row.customer.id) ?? [], { date, today }, money.compact);
}
